import { createFileRoute } from "@tanstack/react-router";
import { Phone, Mail, MapPin, MessageCircle, Instagram, Music2 } from "lucide-react";
import Layout from "@/components/Layout";
import { BUSINESS } from "@/lib/business";
import { buildBreadcrumbJsonLd, canonicalLink, pageSocialMeta } from "@/lib/seo";

const BREADCRUMB_JSONLD = buildBreadcrumbJsonLd([
  { name: "Home", path: "/" },
  { name: "Contact", path: "/contact" },
]);

const CONTACT_TITLE = "Contact BLVDS Detailing — NYC Mobile Detailing";
const CONTACT_DESCRIPTION = "Call, text, or email BLVDS Detailing for mobile car detailing in NYC. Queens, Brooklyn & Long Island — we come to your driveway.";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: CONTACT_TITLE },
      { name: "description", content: CONTACT_DESCRIPTION },
      ...pageSocialMeta("/contact", CONTACT_TITLE, CONTACT_DESCRIPTION),
    ],
    links: [canonicalLink("/contact")],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(BREADCRUMB_JSONLD) },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <Layout>
      <section className="pt-16 pb-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-base sm:text-lg font-semibold text-accent uppercase tracking-wider mb-3">Contact</p>
        <h1 className="text-5xl sm:text-6xl font-bold mb-5 inline-block pr-4">Let's Talk <span className="text-gradient-primary pr-2">Cars</span>.</h1>
        <p className="text-lg text-muted-foreground max-w-xl mx-auto">
          Questions, custom quotes, or just not sure where to start we're here.
        </p>
      </section>

      <section className="pb-12 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid sm:grid-cols-2 gap-5">
          <a
            href={`tel:${BUSINESS.phone}`}
            className="p-7 rounded-2xl bg-surface border border-border hover:border-primary/50 transition-smooth group"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 group-hover:shadow-glow transition-smooth">
              <Phone className="w-6 h-6 text-primary-foreground" />
            </div>
            <h2 className="font-semibold mb-1">Call us</h2>
            <p className="text-2xl font-bold text-gradient-primary">{BUSINESS.phoneDisplay}</p>
            <p className="text-xs text-muted-foreground mt-1">Mon–Sat · 10am – 8pm</p>
          </a>

          <a
            href={`sms:${BUSINESS.phone}?&body=Hi%20BLVDS%2C%20I%27d%20like%20to%20book%20a%20detail.`}
            className="p-7 rounded-2xl bg-surface border border-border hover:border-primary/50 transition-smooth group"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 group-hover:shadow-glow transition-smooth">
              <MessageCircle className="w-6 h-6 text-primary-foreground" />
            </div>
            <h2 className="font-semibold mb-1">Text us</h2>
            <p className="text-lg font-semibold">Send us an SMS</p>
            <p className="text-xs text-muted-foreground mt-1">Fastest reply</p>
          </a>

          <a
            href={`mailto:${BUSINESS.email}`}
            className="p-7 rounded-2xl bg-surface border border-border hover:border-accent/50 transition-smooth group"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-accent flex items-center justify-center mb-4 group-hover:shadow-accent-glow transition-smooth">
              <Mail className="w-6 h-6 text-accent-foreground" />
            </div>
            <h2 className="font-semibold mb-1">Email</h2>
            <p className="text-base font-semibold break-all">{BUSINESS.email}</p>
          </a>

          <div className="p-7 rounded-2xl bg-surface border border-border">
            <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center mb-4">
              <MapPin className="w-6 h-6 text-primary-foreground" />
            </div>
            <h2 className="font-semibold mb-1">Service Area</h2>
            <p className="text-base font-semibold">{BUSINESS.serviceArea}</p>
            <p className="text-xs text-muted-foreground mt-1">100% mobile we come to you</p>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center gap-4">
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Follow us</p>
          <div className="flex gap-4">
            <a
              href={BUSINESS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="w-12 h-12 rounded-xl bg-surface border border-border hover:border-primary/50 flex items-center justify-center transition-smooth group"
            >
              <Instagram className="w-5 h-5 text-foreground group-hover:text-primary transition-smooth" />
            </a>
            <a
              href={BUSINESS.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="w-12 h-12 rounded-xl bg-surface border border-border hover:border-primary/50 flex items-center justify-center transition-smooth group"
            >
              <Music2 className="w-5 h-5 text-foreground group-hover:text-primary transition-smooth" />
            </a>
          </div>
        </div>
      </section>

      <section className="pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl overflow-hidden border border-border shadow-elevated">
          <iframe
            src={BUSINESS.mapsEmbed}
            width="100%"
            height="450"
            style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Service Area Map"
          />
        </div>
      </section>
    </Layout>
  );
}
